import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { GRUND } from "../lib/format";
import { Button, Card, ErrorBox, Field, Input, Select, Spinner, Textarea } from "./kd";

/** Auswahl eigener Grund-Vorlagen im Formular (nur Überweiser mit Konto). */
export function GrundVorlagenPicker({ onPick }: { onPick: (v: any) => void }) {
  const [list, setList] = useState<any[]>([]);
  useEffect(() => { api.get("/konto/grund-vorlagen").then((r) => setList(r.data)).catch(() => undefined); }, []);
  if (!list.length) return null;
  return (
    <div className="mb-4" data-testid="grund-vorlagen">
      <p className="mb-2 text-sm font-medium">Meine Vorlagen</p>
      <div className="flex flex-wrap gap-2">
        {list.map((v) => (
          <button key={v.id} type="button" onClick={() => onPick(v)} title={v.text} data-testid={`grund-vorlage-${v.id}`}
            className="min-h-[40px] rounded-full border border-primary px-3 text-sm font-medium text-primary transition-colors hover:bg-primary-light">{v.titel}</button>
        ))}
      </div>
    </div>
  );
}

const EMPTY = { titel: "", text: "", grund: "" };

export function GrundVorlagenManager() {
  const [list, setList] = useState<any[] | null>(null);
  const [form, setForm] = useState<any>(EMPTY);
  const [edit, setEdit] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/konto/grund-vorlagen").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr("");
    if (!form.titel.trim() || !form.text.trim()) { setErr("Bitte Titel und Text angeben."); return; }
    const body = { ...form, grund: form.grund || null };
    try { edit ? await api.put(`/konto/grund-vorlagen/${edit}`, body) : await api.post("/konto/grund-vorlagen", body); toast.success("Vorlage gespeichert."); setForm(EMPTY); setEdit(null); load(); }
    catch (ex) { setErr(errMsg(ex)); }
  };
  const del = async (v: any) => { if (!window.confirm(`Vorlage „${v.titel}“ löschen?`)) return; await api.delete(`/konto/grund-vorlagen/${v.id}`); load(); };
  return (
    <Card data-testid="grund-vorlagen-manager">
      <h2 className="mb-1">Vorlagen für den Überweisungsgrund</h2>
      <p className="mb-4 text-sm text-text-muted">Häufige Überweisungsgründe einmal anlegen und im Formular per Klick übernehmen. Gilt für alle Benutzer Ihrer Praxis.</p>
      {!list ? <Spinner /> : list.length > 0 && (
        <ul className="mb-5 divide-y divide-border rounded-lg border border-border">
          {list.map((v) => (
            <li key={v.id} className="flex items-start gap-3 p-3" data-testid={`gv-${v.id}`}>
              <div className="min-w-0 flex-1"><p className="font-medium">{v.titel}{v.grund && <span className="text-sm font-normal text-text-muted"> · {GRUND[v.grund]}</span>}</p><p className="text-sm text-text-muted">{v.text}</p></div>
              <Button variant="ghost" aria-label="Bearbeiten" onClick={() => { setEdit(v.id); setForm({ titel: v.titel, text: v.text, grund: v.grund || "" }); }} data-testid={`gv-edit-${v.id}`}><Pencil className="h-4 w-4" aria-hidden /></Button>
              <Button variant="ghost" aria-label="Löschen" onClick={() => del(v)} data-testid={`gv-delete-${v.id}`}><Trash2 className="h-4 w-4" aria-hidden /></Button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="rounded-lg bg-bg p-4">
        <h3 className="mb-3">{edit ? "Vorlage bearbeiten" : "Neue Vorlage"}</h3>
        <ErrorBox testid="gv-error">{err}</ErrorBox>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Titel" required htmlFor="gvt"><Input id="gvt" value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} data-testid="gv-titel-input" /></Field>
          <Field label="Grund (optional)" htmlFor="gvg"><Select id="gvg" options={GRUND} placeholder="– keiner –" value={form.grund} onChange={(e) => setForm({ ...form, grund: e.target.value })} data-testid="gv-grund-select" /></Field>
        </div>
        <Field label="Text" required htmlFor="gvx"><Textarea id="gvx" rows={3} maxLength={2000} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="gv-text-input" /></Field>
        <div className="flex gap-2">
          <Button type="submit" data-testid="gv-save"><Plus className="h-4 w-4" aria-hidden />{edit ? "Speichern" : "Hinzufügen"}</Button>
          {edit && <Button variant="ghost" onClick={() => { setEdit(null); setForm(EMPTY); }}>Abbrechen</Button>}
        </div>
      </form>
    </Card>
  );
}
